import { useState, useEffect } from 'react';
import {
  map,
  find,
  has,
  isNil,
  set,
  get,
  cloneDeep,
  isEqual,
  every,
  intersection,
  isEmpty,
  filter
} from 'lodash';

import { OriginalFileField } from '../interface';

function useFileToInferAssetsFields(mappedFiles: OriginalFileField[], type: string, properties: any[]) {
  const [inferedAssets, setInferedAssets] = useState<OriginalFileField[]>([]);

  const inferedPropertiesMap = {
    'Company Name': 'company.name',
    'Company Number': 'company.number',
    'Legal Entity Name': 'legalEntity.name',
    'Legal Entity Number': 'legalEntity.number',
    'Property Name': 'name',
    'State': 'address.state.abbreviation',
    'Assessor': 'assessor.name',
    'Assessor Account Number': 'number',
  }

  const getPropertyPath = (property: keyof typeof inferedPropertiesMap) =>
    inferedPropertiesMap[property];

  useEffect(() => {
    if (!mappedFiles.length) return;

    const inferedResult: OriginalFileField[] = cloneDeep(mappedFiles);
    const mapKeys = Object.keys(inferedPropertiesMap);
    const everyKeyIsMapped = map(mappedFiles, (inferedItem) => {
      const assetKeys = Object.keys(inferedItem);
      return intersection(mapKeys, assetKeys).length === mapKeys.length;
    });

    if (every(everyKeyIsMapped)) {
      setInferedAssets(mappedFiles);
      return;
    }

    map(inferedResult, (inferedItem, index) => {
      const findProperty: any = find(properties, [
        'number',
        get(inferedItem, 'Property Number'),
      ]);
      const taxYear = Number(get(inferedItem, 'Tax Year'));

      // Set properties if it does not exist
      if (findProperty) {
        for (const key in inferedPropertiesMap) {
          const typedKey = key as keyof typeof inferedPropertiesMap;
          let value = null;

          if (!has(inferedItem, typedKey)) {
            switch (typedKey) {
              case 'Assessor':
              case 'Assessor Account Number': {
                // Filter all assessor accounts for the specific tax year
                const assessorAccountsForTaxYear = filter(findProperty.assessorAccounts, [
                  'taxYear',
                  taxYear,
                ]);
                const stateId = get(findProperty, 'address.stateId');

                // Only infer if there's exactly one assessor account for this tax year
                if (assessorAccountsForTaxYear?.length === 1) {
                  value = get(assessorAccountsForTaxYear[0], getPropertyPath(typedKey));
                } else {
                  const matchingAccount = assessorAccountsForTaxYear.find(
                    (account) => account.assessor.stateId === stateId
                  );
                  if (matchingAccount) {
                    value = get(matchingAccount, getPropertyPath(typedKey));
                  }
                }
                break;
              }
              default:
                value = get(findProperty, getPropertyPath(typedKey));
                break;
            }

            if (!isNil(value)) {
              set(inferedResult[index], typedKey, value);
            }
          }
        }
      }
    });

    const isInferedResultReady = !isEmpty(inferedResult) && !isEqual(inferedResult, mappedFiles);

    if (!isInferedResultReady) return;

    map(inferedResult, (_, index) => {
      set(inferedResult[index], 'type', type);
    });

    setInferedAssets(inferedResult as unknown as OriginalFileField[]);
  }, [mappedFiles, type, properties]);

  return { inferedAssets, setInferedAssets };
}

export default useFileToInferAssetsFields;
